import type { SVGProps } from 'react';

/**
 * Иконки одной системы (канон: 12–14px, штрих 1.5, stroke currentColor).
 * Пути сняты с эталонов Claude Design (sidebar / toolbar / операции).
 */
type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function base({ size = 14, ...rest }: IconProps, children: React.ReactNode, strokeWidth = 1.5) {
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const IconOrders = (p: IconProps) =>
  base(
    p,
    <>
      <rect x="3" y="2" width="10" height="12" rx="1.5" />
      <path d="M5.5 6h5M5.5 9h5" />
    </>,
  );
export const IconProduction = (p: IconProps) =>
  base(
    p,
    <>
      <circle cx="8" cy="8" r="3.25" />
      <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2" />
    </>,
  );
export const IconPlanning = (p: IconProps) =>
  base(
    p,
    <>
      <rect x="2.5" y="3" width="11" height="10.5" rx="1.5" />
      <path d="M2.5 6.5h11M5.5 1.75v2.5M10.5 1.75v2.5" />
    </>,
  );
export const IconMaterials = (p: IconProps) =>
  base(
    p,
    <>
      <circle cx="4.25" cy="8" r="2.5" />
      <path d="M4.25 5.5h9.25v5H6" />
    </>,
  );
export const IconSuppliers = (p: IconProps) =>
  base(
    p,
    <>
      <rect x="1.5" y="4.5" width="8" height="6.5" rx="1" />
      <path d="M9.5 6.75h2.75l2.25 2.5V11h-1.5" />
      <circle cx="4.75" cy="12.5" r="1.25" />
      <circle cx="11.25" cy="12.5" r="1.25" />
    </>,
  );
export const IconProcurement = (p: IconProps) =>
  base(
    p,
    <>
      <path d="M2 3h2l1.6 7.5h6.6L13.5 5H5" />
      <circle cx="6.5" cy="13" r="1" />
      <circle cx="11" cy="13" r="1" />
    </>,
  );
export const IconClients = (p: IconProps) =>
  base(
    p,
    <>
      <circle cx="8" cy="5.5" r="2.5" />
      <path d="M3.5 13c.5-2.6 2.1-4 4.5-4s4 1.4 4.5 4" />
    </>,
  );
export const IconWorkshops = (p: IconProps) =>
  base(
    p,
    <>
      <path d="M2.5 6.5L8 3l5.5 3.5v7h-11z" />
      <path d="M5.75 13.5V9.75h4.5v3.75" />
    </>,
  );
export const IconContractWork = (p: IconProps) =>
  base(
    p,
    <>
      <path d="M2.5 5.5h11v7h-11z" />
      <path d="M5.5 5.5V3.5h5v2M2.5 8.5h11" />
    </>,
  );
export const IconPortal = (p: IconProps) =>
  base(
    p,
    <>
      <rect x="2" y="2.5" width="12" height="9" rx="1.5" />
      <path d="M5.5 14h5M8 11.5V14" />
    </>,
  );
export const IconFinance = (p: IconProps) =>
  base(
    p,
    <>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M6.5 11V5h2a1.9 1.9 0 0 1 0 3.8H6.5M5.75 10h3" />
    </>,
  );
export const IconAnalytics = (p: IconProps) =>
  base(p, <path d="M2.5 13.5V9M6.25 13.5V5.5M10 13.5V7.5M13.75 13.5V3" />);
/** Сообщения (этап 22): пузырь диалога с хвостиком. */
export const IconMessages = (p: IconProps) =>
  base(
    p,
    <>
      <path d="M2.5 3.5h11v7.5H6.5L3.5 13.5v-2.5h-1z" />
      <path d="M5.5 6.25h5M5.5 8.75h3" />
    </>,
  );

export const IconSearch = (p: IconProps) =>
  base(
    { size: 12, ...p },
    <>
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.4 10.4L14 14" />
    </>,
  );
export const IconBell = (p: IconProps) =>
  base(
    { size: 13, ...p },
    <>
      <path d="M8 2a4 4 0 0 0-4 4c0 3-1.2 4-1.2 4h10.4S12 9 12 6a4 4 0 0 0-4-4z" />
      <path d="M6.8 13a1.3 1.3 0 0 0 2.4 0" />
    </>,
    1.4,
  );
export const IconInspector = (p: IconProps) =>
  base(
    { size: 13, ...p },
    <>
      <rect x="2" y="3" width="12" height="10" rx="1.5" />
      <path d="M10.5 3v10" />
    </>,
    1.4,
  );
export const IconActions = (p: IconProps) =>
  base(
    { size: 13, ...p },
    <>
      <rect x="2.5" y="2.5" width="11" height="11" rx="1.5" />
      <path d="M5 8.2l2 2 4-4.4" />
    </>,
    1.4,
  );

/* Операции производства */
export const IconCut = (p: IconProps) =>
  base(
    { size: 12, ...p },
    <>
      <circle cx="4.5" cy="4.5" r="2" />
      <circle cx="4.5" cy="11.5" r="2" />
      <path d="M6.2 5.9L14 13M6.2 10.1L14 3" />
    </>,
  );
export const IconSew = (p: IconProps) =>
  base(
    { size: 12, ...p },
    <>
      <path d="M2 12.5h12" />
      <path d="M3.5 12.5c0-4 2-8 4.5-8s4.5 4 4.5 8" strokeDasharray="2 1.8" />
    </>,
  );
export const IconPrint = (p: IconProps) =>
  base(
    { size: 12, ...p },
    <>
      <rect x="3" y="2.5" width="10" height="6" rx="1" />
      <path d="M5.5 11h5M4 13.5h8" />
    </>,
  );
export const IconQc = (p: IconProps) =>
  base(
    { size: 12, ...p },
    <>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M5.5 8l1.8 1.8L10.8 6" />
    </>,
  );
export const IconPack = (p: IconProps) =>
  base(
    { size: 12, ...p },
    <>
      <path d="M2.5 5L8 2.5 13.5 5v6L8 13.5 2.5 11z" />
      <path d="M2.5 5L8 7.5 13.5 5M8 7.5v6" />
    </>,
  );
export const IconIssue = (p: IconProps) =>
  base(
    { size: 12, ...p },
    <>
      <path d="M3 4.5h10v8H3z" />
      <path d="M3 7.5h10M8 2.5v2" />
    </>,
  );
/** Подрядчик (teal outline house) */
export const IconContractor = (p: IconProps) =>
  base({ size: 11, ...p }, <path d="M2.5 6.5L8 3l5.5 3.5v7h-11z" />, 1.8);

/** Ромб предупреждения 9px (warning/critical/orange) */
export function Diamond({ color, size = 9 }: { color: string; size?: number }) {
  return (
    <svg className="icon" width={size} height={size} viewBox="0 0 9 9" aria-hidden="true">
      <rect x="4.5" y="0.4" width="5.8" height="5.8" transform="rotate(45 4.5 0.4)" fill={color} />
    </svg>
  );
}
export const IconCheck = (p: IconProps) =>
  base({ size: 10, ...p }, <path d="M3 8.5l3.2 3.2L13 4.5" />, 2.2);
